import { apiRequest } from '../../shared/api/client';

/**
 * Accepting an invitation — mirrors Invetrak.Api.Endpoints.InvitationEndpoints.
 *   GET  /api/v1/invitations/{token}          anonymous
 *   POST /api/v1/invitations/{token}/accept   anonymous
 *
 * BOTH ARE ANONYMOUS ON PURPOSE. The invitee has no credentials yet — that is the whole point of
 * the flow — so the 64-character random token in the URL is the authorization. It is why the token
 * expires, why accepting consumes it, and why nothing here is behind the session cookie.
 *
 * The sending half lives in features/users/api.ts, behind tenant:manage_users.
 */

export interface InvitationViewDto {
  tenantInvitationId: string;
  tenantId: string;
  tenantDisplayName: string;
  email: string;
  expiresAtUtc: string;
  status: string;
}

export interface AcceptInvitationDto {
  tenantInvitationId: string;
  userId: string;
  email: string;
  groupCodes: string[];
}

/**
 * What this token is for, before asking for a password.
 *
 * Answers 404 when the token is unknown, 410 once it has expired and 409 once it has been used —
 * three different sentences the screen owes the reader, since "your link does not work" leaves
 * somebody guessing whether to ask for another one.
 */
export async function getInvitationByToken(
  token: string,
  signal?: AbortSignal,
): Promise<InvitationViewDto> {
  const { data } = await apiRequest<InvitationViewDto>(
    `/v1/invitations/${encodeURIComponent(token)}`,
    { method: 'GET', signal },
  );
  return data;
}

/**
 * Creates the account and consumes the token. The groups chosen by whoever sent the invitation are
 * applied here, server-side; they are not resent, so they cannot be tampered with on the way.
 *
 * No session results — the server does not sign the new user in — so the caller sends them to the
 * login screen afterwards.
 */
export async function acceptInvitation(
  token: string,
  password: string,
  firstName: string,
  lastName: string,
  signal?: AbortSignal,
): Promise<AcceptInvitationDto> {
  const { data } = await apiRequest<AcceptInvitationDto>(
    `/v1/invitations/${encodeURIComponent(token)}/accept`,
    { method: 'POST', body: { password, firstName, lastName }, signal },
  );
  return data;
}
